/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { i18n } from '@lingui/core'
import { TabContext, TabPanel } from '@mui/lab'
import {
  Box,
  FormControl,
  MenuItem,
  NoSsr,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  styled,
} from '@mui/material'
import { useEffect, useState, useMemo } from 'react'
import useCallAPI from '../../hooks/useCallAPI'
import { SecondaryButton } from '../Button'
import { ReviewItem, ReviewItemProps } from './ReviewItem'

export type CategoryReviewItemTabsProps = {
  reviews: Array<ReviewItemProps> | undefined | null
}

interface StyledTabsProps {
  children?: React.ReactNode
  value: string
  onChange: (event: React.SyntheticEvent, newValue: string) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth' | undefined
  scrollButtons?: boolean
  allowScrollButtonsMobile?: boolean
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }} />
))({
  minHeight: 35,
  height: 35,
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 2,
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#A95CFF',
  },
})

interface StyledTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
}

export const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    minHeight: 35,
    height: 35,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    color: '#6A6A6A',
    ':hover': {
      color: '#000000',
    },
    '&.Mui-selected': {
      fontWeight: 'bold',
      color: '#A95CFF',
      borderBottom: '2px solid #A95CFF',
      ':hover': {
        color: '#613394',
        borderColor: '#613394',
      },
    },
    '&.Mui-focusVisible': {},
  }),
)

interface StyledTabPanelProps {
  value: string
  children?: React.ReactNode
}

export const StyledTabPanel = styled((props: StyledTabPanelProps) => <TabPanel {...props} />)(
  ({ theme }) => ({
    '&.MuiTabPanel-root': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 12,
      paddingBottom: 12,
    },
  }),
)

export function CategoryReviewItemTabs(props: CategoryReviewItemTabsProps) {
  const { reviews } = props
  const [value, setValue] = useState('0')
  const [displayCount, setDisplayCount] = useState(5)
  const REVIEWS_PER_PAGE = 5

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  })

  // Filter and sort reviews based on selected tab
  const sortedReviews = useMemo(() => {
    const filteredReviews: ReviewItemProps[] =
      reviews?.filter((item): item is ReviewItemProps => item !== null) ?? []

    switch (value) {
      case '0': // Newest
        return [...filteredReviews].sort(
          (a, b) =>
            // Assuming there's a date field, adjust as needed
            (b.yotpo_id || 0) - (a.yotpo_id || 0),
        )
      case '1': // Best
        return [...filteredReviews].sort((a, b) => (b.score || 0) - (a.score || 0))
      case '2': // Worst
        return [...filteredReviews].sort((a, b) => (a.score || 0) - (b.score || 0))
      case '3': // Most Helpful
        return [...filteredReviews].sort((a, b) => (b.votes_up || 0) - (a.votes_up || 0))
      default:
        return filteredReviews
    }
  }, [reviews, value])

  // Get only the reviews to display based on displayCount
  const displayedReviews = useMemo(
    () => sortedReviews.slice(0, displayCount),
    [sortedReviews, displayCount],
  )

  // Check if there are more reviews to load
  const hasMoreReviews = displayCount < sortedReviews.length

  const handleLoadNextPage = () => {
    setDisplayCount((prev) => prev + REVIEWS_PER_PAGE)
  }

  const handleChange = (event: SelectChangeEvent<string>, newValue: string) => {
    setDisplayCount(5) // Reset to initial 5 items when changing tabs
    setValue(newValue)
  }

  return (
    <TabContext value={value}>
      <Box className='flex w-full items-center justify-end'>
        <FormControl size='small' className='min-w-[150px]'>
          <Select
            value={value}
            onChange={(e) => handleChange(e, e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Review Sort Options' }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: '150px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                },
              },
            }}
          >
            <MenuItem value='0'>Newest</MenuItem>
            <MenuItem value='1'>Best</MenuItem>
            <MenuItem value='2'>Worst</MenuItem>
            <MenuItem value='3'>Most Helpful</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* PANELS WITH PAGINATION */}
      <StyledTabPanel value='0'>
        <div className='flex flex-col gap-[20px]'>
          <div>
            {displayedReviews.map((review) => (
              <NoSsr key={review.yotpo_id}>
                <ReviewItem {...review} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='1'>
        <div className='flex flex-col gap-[20px]'>
          <div>
            {displayedReviews.map((review) => (
              <NoSsr key={review.yotpo_id}>
                <ReviewItem {...review} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='2'>
        <div className='flex flex-col gap-[20px]'>
          <div>
            {displayedReviews.map((review) => (
              <NoSsr key={review.yotpo_id}>
                <ReviewItem {...review} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      <StyledTabPanel value='3'>
        <div className='flex flex-col gap-[20px]'>
          <div>
            {displayedReviews.map((review) => (
              <NoSsr key={review.yotpo_id}>
                <ReviewItem {...review} />
              </NoSsr>
            ))}
          </div>
        </div>
      </StyledTabPanel>

      {hasMoreReviews && (
        <SecondaryButton
          className='w-full border-bright-templi-purple text-bright-templi-purple lg:w-[250px]'
          onClick={handleLoadNextPage}
          loading={false}
        >
          {i18n._('Read more reviews...')}
        </SecondaryButton>
      )}
    </TabContext>
  )
}
